// src/pages/stockAdjustment/StockAdjustmentDetailsPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Package,
  CheckCircle,
  Trash2,
  Printer,
  FileSpreadsheet,
  AlertCircle,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  User,
  Warehouse as WarehouseIcon,
  StickyNote,
  Search,
  X,
} from "lucide-react";
import {
  getStockAdjustmentById,
  finalizeStockAdjustment,
} from "../../services/api/stockAdjustment";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useAlert } from "../../contexts/AlertContext";
import { getImageUrl, handleImageError } from "../../utils/imageUtils";
import { formatDate, formatDateTime } from "../../utils/dateUtils";
import PageHeader from "../../components/common/PageHeader/PageHeader";
import styles from "./StockAdjustmentDetailsPage.module.css";

const StockAdjustmentDetailsPage = () => {
  const navigate = useNavigate();
  const { adjustmentId } = useParams();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  // State
  const [adjustment, setAdjustment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  useEffect(() => {
    loadAdjustment();
  }, [adjustmentId]);

  const loadAdjustment = async () => {
    try {
      setLoading(true);
      const response = await getStockAdjustmentById(adjustmentId);
      setAdjustment(response.adjustment);
    } catch (err) {
      console.error("Failed to load adjustment:", err);
      error(err.response?.data?.message || t("stockAdjustment.failedToLoad"));
      navigate("/inventory/stock-adjustments");
    } finally {
      setLoading(false);
    }
  };

  const handleFinalize = async () => {
    try {
      setIsFinalizing(true);
      await finalizeStockAdjustment(adjustmentId);
      success(t("stockAdjustment.adjustmentFinalized"));
      setShowFinalizeDialog(false);
      loadAdjustment();
    } catch (err) {
      console.error("Failed to finalize:", err);
      error(
        err.response?.data?.message || t("stockAdjustment.failedToFinalize")
      );
    } finally {
      setIsFinalizing(false);
    }
  };

  const handleExportCSV = () => {
    if (!adjustment || !adjustment.items) return;

    const headers = [
      t("stockAdjustment.product"),
      t("stockAdjustment.sku"),
      t("stockAdjustment.barcode"),
      t("stockAdjustment.recorded"),
      t("stockAdjustment.physical"),
      t("stockAdjustment.variance"),
      t("stockAdjustment.notes"),
    ];

    const rows = adjustment.items.map((item) => {
      const displayName = getDisplayName(item);
      return [
        displayName,
        getSku(item),
        getBarcode(item),
        item.recordedCount,
        item.physicalCount,
        item.variance,
        item.notes || "",
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `stock-adjustment-${adjustment.adjustmentNumber}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  const getDisplayName = (item) => {
    if (!item.Product) return t("stockAdjustment.unknownProduct");

    if (item.variationId && item.ProductVariation) {
      const attributes =
        item.ProductVariation.attributeValues
          ?.map((av) => `${av.ProductAttribute?.name}: ${av.value}`)
          .filter(Boolean) || [];

      const attributeDisplay = attributes.join(", ");
      return attributeDisplay
        ? `${item.Product.name} (${attributeDisplay})`
        : item.Product.name;
    }

    return item.Product.name;
  };

  const getSku = (item) => {
    if (item.variationId && item.ProductVariation) {
      return item.ProductVariation.sku;
    }
    return item.Product?.sku || t("stockAdjustment.noMatch");
  };

  const getBarcode = (item) => {
    if (item.variationId && item.ProductVariation) {
      return item.ProductVariation.barcode || "-";
    }
    return item.Product?.barcode || "-";
  };

  const getImage = (item) => {
    if (item.variationId && item.ProductVariation) {
      return (
        item.ProductVariation.images?.[0]?.path ||
        item.Product?.images?.[0]?.path
      );
    }
    return item.Product?.images?.[0]?.path;
  };

  const getVarianceDisplay = (variance) => {
    const value = parseFloat(variance);
    if (value === 0) {
      return { text: "0", icon: Minus, color: "#6b7280" };
    } else if (value > 0) {
      return {
        text: `+${value.toFixed(2)}`,
        icon: TrendingUp,
        color: "#16a34a",
      };
    } else {
      return { text: value.toFixed(2), icon: TrendingDown, color: "#dc2626" };
    }
  };

  const hasSignificantVariance = (item) => {
    const variance = Math.abs(parseFloat(item.variance));
    const recorded = parseFloat(item.recordedCount);
    if (recorded === 0) return variance > 0;
    return variance / recorded > 0.5; // 50% threshold
  };

  const sortItems = (items) => {
    if (!sortConfig.key) return items;

    return [...items].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case "name":
          aValue = getDisplayName(a);
          bValue = getDisplayName(b);
          break;
        case "sku":
          aValue = getSku(a);
          bValue = getSku(b);
          break;
        case "recorded":
          aValue = parseFloat(a.recordedCount);
          bValue = parseFloat(b.recordedCount);
          break;
        case "physical":
          aValue = parseFloat(a.physicalCount);
          bValue = parseFloat(b.physicalCount);
          break;
        case "variance":
          aValue = parseFloat(a.variance);
          bValue = parseFloat(b.variance);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const filterItems = (items) => {
    if (!searchTerm.trim()) return items;

    const term = searchTerm.toLowerCase();
    return items.filter((item) => {
      const displayName = getDisplayName(item).toLowerCase();
      const sku = getSku(item).toLowerCase();
      const barcode = getBarcode(item).toLowerCase();
      return (
        displayName.includes(term) ||
        sku.includes(term) ||
        barcode.includes(term)
      );
    });
  };

  if (loading) {
    return (
      <div className={styles.detailsPage}>
        <PageHeader
          title={t("stockAdjustment.stockAdjustmentDetails")}
          description={t("stockAdjustment.detailsPageDescription")}
          icon={Package}
          onBack={() => navigate("/inventory/stock-adjustments")}
        />
        <div className={styles.loadingState}>
          <Loader2 className={styles.spinner} size={48} />
          <h3>{t("stockAdjustment.loadingDetails")}</h3>
        </div>
      </div>
    );
  }

  if (!adjustment) {
    return (
      <div className={styles.detailsPage}>
        <PageHeader
          title={t("stockAdjustment.stockAdjustmentDetails")}
          description={t("stockAdjustment.detailsPageDescription")}
          icon={Package}
          onBack={() => navigate("/inventory/stock-adjustments")}
        />
        <div className={styles.errorState}>
          <AlertCircle size={64} />
          <h3>{t("stockAdjustment.adjustmentNotFound")}</h3>
          <button
            onClick={() => navigate("/inventory/stock-adjustments")}
            className={styles.primaryButton}
          >
            {t("stockAdjustment.goBackToList")}
          </button>
        </div>
      </div>
    );
  }

  const filteredItems = filterItems(adjustment.items || []);
  const sortedItems = sortItems(filteredItems);
  const totalVariance =
    adjustment.items?.reduce(
      (sum, item) => sum + parseFloat(item.variance),
      0
    ) || 0;
  const varianceDisplay = getVarianceDisplay(totalVariance);
  const VarianceIcon = varianceDisplay.icon;

  return (
    <div className={styles.detailsPage}>
      {/* Header */}
      <PageHeader
        title={adjustment.adjustmentNumber}
        description={t("stockAdjustment.stockAdjustmentDetails")}
        icon={Package}
        onBack={() => navigate("/inventory/stock-adjustments")}
        actions={
          <div className={styles.headerActions}>
            {adjustment.status === "draft" && (
              <>
                <button
                  onClick={() => setShowFinalizeDialog(true)}
                  className={styles.finalizeButton}
                >
                  <CheckCircle size={18} />
                  {t("stockAdjustment.finalize")}
                </button>
                <button className={styles.deleteButton}>
                  <Trash2 size={18} />
                </button>
              </>
            )}
            {adjustment.status === "finalized" && (
              <>
                <button
                  onClick={handlePrint}
                  className={styles.secondaryButton}
                >
                  <Printer size={18} />
                  {t("stockAdjustment.print")}
                </button>
                <button
                  onClick={handleExportCSV}
                  className={styles.secondaryButton}
                >
                  <FileSpreadsheet size={18} />
                  {t("stockAdjustment.exportCSV")}
                </button>
              </>
            )}
          </div>
        }
      />

      {/* Info Cards */}
      <div className={styles.infoSection}>
        <div className={styles.infoCard}>
          <div className={styles.infoCardHeader}>
            <div
              className={styles.infoCardIcon}
              style={{
                background:
                  adjustment.status === "finalized" ? "#d1fae5" : "#fef3c7",
              }}
            >
              <Package
                size={24}
                style={{
                  color:
                    adjustment.status === "finalized" ? "#16a34a" : "#f59e0b",
                }}
              />
            </div>
            <div>
              <h3>{t("stockAdjustment.status")}</h3>
              <span
                className={styles.statusBadge}
                style={{
                  backgroundColor:
                    adjustment.status === "finalized" ? "#d1fae5" : "#fef3c7",
                  color:
                    adjustment.status === "finalized" ? "#16a34a" : "#f59e0b",
                }}
              >
                {adjustment.status === "finalized"
                  ? t("stockAdjustment.statusFinalized")
                  : t("stockAdjustment.statusDraft")}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.infoCard}>
          <div className={styles.infoCardHeader}>
            <div
              className={styles.infoCardIcon}
              style={{ background: "#dbeafe" }}
            >
              <WarehouseIcon size={24} style={{ color: "#3b82f6" }} />
            </div>
            <div>
              <h3>{t("stockAdjustment.warehouse")}</h3>
              <p className={styles.infoValue}>{adjustment.Warehouse?.name}</p>
              {adjustment.Warehouse?.address && (
                <p className={styles.infoSubtext}>
                  {adjustment.Warehouse.address}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={styles.infoCard}>
          <div className={styles.infoCardHeader}>
            <div
              className={styles.infoCardIcon}
              style={{ background: "#f3e8ff" }}
            >
              <Calendar size={24} style={{ color: "#9333ea" }} />
            </div>
            <div>
              <h3>{t("stockAdjustment.adjustmentDate")}</h3>
              <p className={styles.infoValue}>
                {formatDate(adjustment.adjustmentDate)}
              </p>
            </div>
          </div>
        </div>

        <div className={styles.infoCard}>
          <div className={styles.infoCardHeader}>
            <div
              className={styles.infoCardIcon}
              style={{ background: "#ede9fe" }}
            >
              <User size={24} style={{ color: "#7F5CFF" }} />
            </div>
            <div>
              <h3>{t("stockAdjustment.createdBy")}</h3>
              <p className={styles.infoValue}>{adjustment.createdBy?.name}</p>
              <p className={styles.infoSubtext}>
                {formatDateTime(adjustment.createdAt)}
              </p>
            </div>
          </div>
        </div>

        {adjustment.finalizedBy && (
          <div className={styles.infoCard}>
            <div className={styles.infoCardHeader}>
              <div
                className={styles.infoCardIcon}
                style={{ background: "#d1fae5" }}
              >
                <CheckCircle size={24} style={{ color: "#16a34a" }} />
              </div>
              <div>
                <h3>{t("stockAdjustment.finalizedBy")}</h3>
                <p className={styles.infoValue}>
                  {adjustment.finalizedBy.name}
                </p>
                <p className={styles.infoSubtext}>
                  {formatDateTime(adjustment.finalizedAt)}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className={styles.infoCard}>
          <div className={styles.infoCardHeader}>
            <div
              className={styles.infoCardIcon}
              style={{ background: "#f3f4f6" }}
            >
              <Package size={24} style={{ color: "#6b7280" }} />
            </div>
            <div>
              <h3>{t("stockAdjustment.totalItems")}</h3>
              <p className={styles.infoValue}>{adjustment.totalItems || 0}</p>
            </div>
          </div>
        </div>

        <div className={styles.infoCard}>
          <div className={styles.infoCardHeader}>
            <div
              className={styles.infoCardIcon}
              style={{ background: varianceDisplay.color + "20" }}
            >
              <VarianceIcon
                size={24}
                style={{ color: varianceDisplay.color }}
              />
            </div>
            <div>
              <h3>{t("stockAdjustment.totalVariance")}</h3>
              <p
                className={styles.infoValue}
                style={{ color: varianceDisplay.color }}
              >
                {varianceDisplay.text}
              </p>
            </div>
          </div>
        </div>
      </div>

      {adjustment.notes && (
        <div className={styles.notesCard}>
          <div className={styles.notesHeader}>
            <StickyNote size={20} />
            <h3>{t("stockAdjustment.notes")}</h3>
          </div>
          <p>{adjustment.notes}</p>
        </div>
      )}

      {/* Items Section */}
      <div className={styles.itemsSection}>
        <div className={styles.itemsHeader}>
          <h2>
            <Package size={20} />
            {t("stockAdjustment.adjustmentItems")}
          </h2>
          <div className={styles.itemsControls}>
            <div className={styles.searchBox}>
              <Search size={18} />
              <input
                type="text"
                placeholder={t("stockAdjustment.searchItems")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className={styles.clearButton}
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <span className={styles.itemsCount}>
              {sortedItems.length}{" "}
              {t("stockAdjustment.ofItems", {
                total: adjustment.items?.length || 0,
              })}
            </span>
          </div>
        </div>

        {sortedItems.length > 0 ? (
          <>
            <div className={styles.tableContainer}>
              <table className={styles.itemsTable}>
                <thead>
                  <tr>
                    <th>{t("stockAdjustment.itemNumber")}</th>
                    <th
                      onClick={() => handleSort("name")}
                      className={styles.sortable}
                    >
                      {t("stockAdjustment.product")}
                      {sortConfig.key === "name" && (
                        <span className={styles.sortIcon}>
                          {sortConfig.direction === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => handleSort("sku")}
                      className={styles.sortable}
                    >
                      {t("stockAdjustment.sku")}
                      {sortConfig.key === "sku" && (
                        <span className={styles.sortIcon}>
                          {sortConfig.direction === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th>{t("stockAdjustment.barcode")}</th>
                    <th
                      onClick={() => handleSort("recorded")}
                      className={`${styles.sortable} ${styles.textCenter}`}
                    >
                      {t("stockAdjustment.recorded")}
                      {sortConfig.key === "recorded" && (
                        <span className={styles.sortIcon}>
                          {sortConfig.direction === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => handleSort("physical")}
                      className={`${styles.sortable} ${styles.textCenter}`}
                    >
                      {t("stockAdjustment.physical")}
                      {sortConfig.key === "physical" && (
                        <span className={styles.sortIcon}>
                          {sortConfig.direction === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th
                      onClick={() => handleSort("variance")}
                      className={`${styles.sortable} ${styles.textCenter}`}
                    >
                      {t("stockAdjustment.variance")}
                      {sortConfig.key === "variance" && (
                        <span className={styles.sortIcon}>
                          {sortConfig.direction === "asc" ? "↑" : "↓"}
                        </span>
                      )}
                    </th>
                    <th>{t("stockAdjustment.notes")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item, index) => {
                    const itemVariance = getVarianceDisplay(item.variance);
                    const ItemVarianceIcon = itemVariance.icon;
                    const image = getImage(item);

                    return (
                      <tr
                        key={item.id}
                        className={
                          hasSignificantVariance(item)
                            ? styles.significantVariance
                            : ""
                        }
                      >
                        <td className={styles.textCenter}>{index + 1}</td>
                        <td>
                          <div className={styles.productCell}>
                            {image && (
                              <img
                                src={getImageUrl(image)}
                                alt={getDisplayName(item)}
                                className={styles.productImage}
                                onError={handleImageError}
                              />
                            )}
                            <div className={styles.productInfo}>
                              <span className={styles.productName}>
                                {getDisplayName(item)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={styles.sku}>{getSku(item)}</span>
                        </td>
                        <td>
                          <span className={styles.barcode}>
                            {getBarcode(item)}
                          </span>
                        </td>
                        <td className={styles.textCenter}>
                          {item.recordedCount}
                        </td>
                        <td className={styles.textCenter}>
                          {item.physicalCount}
                        </td>
                        <td className={styles.textCenter}>
                          <div
                            className={styles.varianceCell}
                            style={{ color: itemVariance.color }}
                          >
                            <ItemVarianceIcon size={16} />
                            <strong>{itemVariance.text}</strong>
                          </div>
                        </td>
                        <td>
                          {item.notes ? (
                            <div
                              className={styles.notesCell}
                              title={item.notes}
                            >
                              <StickyNote size={14} />
                              <span>{item.notes}</span>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className={styles.summaryRow}>
                    <td colSpan="4">
                      <strong>{t("stockAdjustment.total")}</strong>
                    </td>
                    <td className={styles.textCenter}>
                      <strong>
                        {sortedItems
                          .reduce(
                            (sum, item) => sum + parseFloat(item.recordedCount),
                            0
                          )
                          .toFixed(2)}
                      </strong>
                    </td>
                    <td className={styles.textCenter}>
                      <strong>
                        {sortedItems
                          .reduce(
                            (sum, item) => sum + parseFloat(item.physicalCount),
                            0
                          )
                          .toFixed(2)}
                      </strong>
                    </td>
                    <td className={styles.textCenter}>
                      <div
                        className={styles.varianceCell}
                        style={{ color: varianceDisplay.color }}
                      >
                        <VarianceIcon size={16} />
                        <strong>{varianceDisplay.text}</strong>
                      </div>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <div className={styles.emptyState}>
            <Package size={48} />
            <h3>{t("stockAdjustment.noProductsFound")}</h3>
            <p>{t("stockAdjustment.tryAdjustingSearch")}</p>
          </div>
        )}
      </div>

      {/* Finalize Dialog */}
      {showFinalizeDialog && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <AlertCircle size={24} style={{ color: "#f59e0b" }} />
              <h3>{t("stockAdjustment.confirmFinalizeTitle")}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>{t("stockAdjustment.confirmFinalize")}</p>
              <div className={styles.summaryBox}>
                <div className={styles.summaryRow}>
                  <span>{t("stockAdjustment.totalItems")}:</span>
                  <strong>{adjustment.totalItems || 0}</strong>
                </div>
                <div className={styles.summaryRow}>
                  <span>{t("stockAdjustment.totalVariance")}:</span>
                  <strong style={{ color: varianceDisplay.color }}>
                    {varianceDisplay.text}
                  </strong>
                </div>
              </div>
            </div>
            <div className={styles.dialogActions}>
              <button
                onClick={() => setShowFinalizeDialog(false)}
                className={styles.cancelButton}
                disabled={isFinalizing}
              >
                {t("stockAdjustment.cancel")}
              </button>
              <button
                onClick={handleFinalize}
                className={styles.confirmButton}
                disabled={isFinalizing}
              >
                {isFinalizing ? (
                  <>
                    <Loader2 className={styles.spinning} size={16} />
                    {t("stockAdjustment.finalizing")}
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    {t("stockAdjustment.confirmAction")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockAdjustmentDetailsPage;
